import clsx from "clsx";
import {
  DetailedHTMLProps,
  HTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import { useInView } from "react-hook-inview";
import { useDevice } from "../../utils/react/get-device/get-device";
import Cta from "../Cta/Cta";

import {
  NextContentfulImage,
  NextContentfulLoaderProps,
} from "@components/Image";
import QuoteModal from "@components/QuoteModal/QuoteModal";
import { GeneralLink } from "@customTypes/generalLink";
import { ImageWrapperType } from "@customTypes/imageWrapper";
import styles from "./C27_NavigationalQuote.module.scss";

const SHORT_QUOTE_LIMIT = 166;
const LONG_QUOTE_LIMIT = 730;
const GAP_BELOW_TEXT = 10;
const FOOTNOTE_POSITION = 0.5;

interface QuoteProps {
  quoteId?: number;
  content: QuoteType;
  onReadMoreClick?: (clicked?: number) => void;
}

type Props = QuoteProps &
  Omit<
    DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    "content"
  >;

export interface QuoteType {
  authorName: string;
  quote: string;
  authorJobTitle?: string;
  ctaLink?: GeneralLink;
  label?: string;
  optionalCta?: GeneralLink;
  desktopImage?: ImageWrapperType;
  fullWidth?: boolean;
  longQuote?: string;
}

const loaderConfig: NextContentfulLoaderProps = { fit: "fill" };

export const Quote = ({
  quoteId,
  content,
  onReadMoreClick,
  ...rest
}: Props) => {
  const {
    fullWidth,
    label,
    quote,
    authorName,
    authorJobTitle,
    desktopImage,
    ctaLink,
    optionalCta,
    longQuote,
  } = content;
  const [showText, setShowText] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);

  const isCarousel = typeof quoteId !== "undefined";
  const quoteNotInCarousel = typeof quoteId === "undefined";

  const closeLightbox = () => {
    setShowLightbox(false);
  };

  const openLightbox = () => {
    setShowLightbox(true);
  };

  const handleClick = () => {
    const isReadMore = !isMobile && quoteId != undefined;

    if (isReadMore) {
      onReadMoreClick?.(quoteId);
    }

    if (isMobile) {
      setShowText(!showText);
    }
  };

  const [ref, isVisible] = useInView({
    threshold: 0,
    unobserveOnEnter: true,
  });

  const line = useRef<HTMLDivElement>(null);
  const blockquote = useRef<HTMLQuoteElement>(null);
  const footnote = useRef<HTMLDivElement>(null);
  const readMoreButton = useRef<HTMLButtonElement | null>(null);

  const device = useDevice();
  const isMobile = device === "mobile";

  const setLineHeight = () => {
    if (!isMobile && line.current && blockquote.current && footnote.current) {
      const container = line.current.offsetParent as HTMLElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const quoteRect = blockquote.current.getBoundingClientRect();
      const footnoteRect = footnote.current.getBoundingClientRect();

      // if Read More exists, we treat its bottom as "end of text"
      const textEndRect = readMoreButton.current
        ? readMoreButton.current.getBoundingClientRect()
        : quoteRect;

      // TOP of the red line = just under the last bit of text (including Read More)
      const lineTop = textEndRect.bottom - containerRect.top + GAP_BELOW_TEXT;

      // BOTTOM target = middle of the footnote
      const lineBottomTarget =
        footnoteRect.top +
        footnoteRect.height * FOOTNOTE_POSITION -
        containerRect.top;

      const height = lineBottomTarget - lineTop;

      line.current.style.top = `${lineTop}px`;
      line.current.style.height = `${height}px`;
    } else if (line.current) {
      line.current.removeAttribute("style");
    }
  };

  useEffect(() => {
    setLineHeight();
  }, [isVisible, isMobile]);

  const fullQuote = longQuote ? quote + " " + longQuote : quote;

  const textToRender = !showText ? "Read More" : "Read Less";

  return (
    <div
      {...rest}
      className={clsx(
        styles["navigational-quote"],
        isVisible && styles.animate,
        !desktopImage && styles["no-img"],
        !isCarousel && !fullWidth && styles.half
      )}
      ref={ref}
    >
      <div className={styles["block-container"]}>
        <div className={styles["text-content"]}>
          {fullWidth && <p className={styles.label}>{label}</p>}
          {isCarousel && (quote.length > SHORT_QUOTE_LIMIT || longQuote) ? (
            <blockquote ref={blockquote}>
              {!showText ? (
                <>
                  <span>
                    {quote.length > SHORT_QUOTE_LIMIT
                      ? `"${quote.substring(0, SHORT_QUOTE_LIMIT)}`
                      : `"${quote}"`}
                  </span>
                  <span>{quote.length > SHORT_QUOTE_LIMIT ? '..."' : ""}</span>
                </>
              ) : (
                <span>
                  {`"${
                    fullQuote.length > LONG_QUOTE_LIMIT
                      ? fullQuote.substring(0, LONG_QUOTE_LIMIT)
                      : fullQuote
                  }"`}
                </span>
              )}
            </blockquote>
          ) : (
            <blockquote ref={blockquote}>&quot;{quote}&quot;</blockquote>
          )}

          {isCarousel && (quote.length > SHORT_QUOTE_LIMIT || longQuote) && (
            <button
              ref={readMoreButton}
              onClick={handleClick}
              className={styles["read-more-btn"]}
            >
              {isMobile ? textToRender : "Read More"}
            </button>
          )}

          {quoteNotInCarousel && longQuote && (
            <button
              ref={readMoreButton}
              onClick={openLightbox}
              className={styles["read-more-btn"]}
            >
              {isMobile ? textToRender : "Read More"}
            </button>
          )}

          {showLightbox && (
            <QuoteModal
              authorJobTitle={authorJobTitle}
              authorName={authorName}
              shortQuote={quote}
              onClose={closeLightbox}
              longQuote={longQuote}
            ></QuoteModal>
          )}
          {desktopImage && fullWidth && (
            <div className={styles.image}>
              <NextContentfulImage
                alt={desktopImage.fields.altText || ""}
                src={desktopImage.fields.url}
                width={isMobile ? 768 : 800}
                height={isMobile ? 517 : 704}
                loaderConfig={loaderConfig}
              />
            </div>
          )}
          <div className={styles.line} ref={line}></div>
          <div ref={footnote} className={styles.footnote}>
            <div>
              <p className={styles.name}>{authorName}</p>
              {authorJobTitle ? <p>{authorJobTitle}</p> : null}
            </div>
            <div>
              {ctaLink && (
                <div className={styles.mainCta}>
                  <Cta {...ctaLink.fields} type="secondary" light={true} />
                </div>
              )}
              {optionalCta && (
                <Cta
                  {...optionalCta.fields}
                  type="tertiary"
                  light={true}
                  icon="icon-arrow-small"
                />
              )}
            </div>
          </div>
        </div>
        {fullWidth && desktopImage && (
          <div className={styles.image}>
            <NextContentfulImage
              alt={desktopImage.fields.altText || ""}
              src={desktopImage.fields.url}
              width={isMobile ? 768 : 800}
              height={isMobile ? 517 : 704}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Quote;
